import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError, type BidSite } from '../lib/api'
import type { PartKind } from '../lib/types'
import type { ArtefactType } from '../lib/artefact-type'
import { availableFormats, exportedKinds, mixesSlides, partFormats } from '../lib/export-formats'
import { downloadExport, exportToSharePoint, type ExportFormat as Format, type ExportScope as Scope } from '../lib/download'
import { useBidProfile } from '../lib/bid-profile'
import { useTemplateLibrary } from '../lib/template-library'
import { navigate, settingsPath } from '../lib/router'
import { isSafeItemName } from '../lib/sharepoint-name'
import { listChildren } from '../lib/graphSites'
import { downloadsBridge, type FinishedDownload } from '../lib/desktop'
import { SharePointBrowser, locationLabel, type BrowseStart, type BrowserLocation } from './SharePointBrowser'
import { ConfirmModal } from './ConfirmModal'

/**
 * Export — a modal for downloading the open part / its deliverable / the whole bid, in the
 * formats the backend accepts for that scope and content (lib/export-formats.ts: Word / web page /
 * Markdown / Excel for prose and grids, PowerPoint for slides), landing either in the
 * member's downloads folder or straight into the bid's own SharePoint (#39). Its trigger sits in
 * the editor header (Canvas `.doc-top`), left of the connection pill (AB#212).
 *
 * Was a dropdown popover (`role="menu"`), where clicking a format fired the download immediately —
 * fine while there was only one place a file could go. Adding a destination choice needs a step in
 * between "pick a format" and "the file leaves this app", so this is now an ordinary modal dialog:
 * scope, format and destination are three plain radio groups, and one "Export" button reads all
 * three and acts. Reusing `modal-backdrop`/`modal` (ConfirmModal, PromptModal, BidSiteCard's own
 * site picker) also fixes the z-index bug the popover had — `.export-pop` sat at z-index 20, behind
 * several other layers (the account menu, the chat target menu, toasts, other modals); the shared
 * `.modal-backdrop` is z-index 60, same as everything else that must sit over the whole app.
 *
 * The download itself lives in lib/download.ts: it resolves the backend origin (a relative /v1 URL
 * would be answered by the SPA host in prod), verifies the body before saving it, and owns the
 * blob/anchor lifecycle. The SharePoint upload is the other half of that same module
 * (`exportToSharePoint`), landing in the bid's `niobe/exports` folder by default (created on first
 * use) or wherever the member picks with the ".../<folder>" link. This component only gathers
 * scope + format + destination and shows the result.
 *
 * Saving to the bid site also names the file (AB#414): a base-name field prefilled with the server's
 * own default (`GET .../sharepoint/export/name` — the "<artefact> - v<version>" rule stays in
 * backend/domain/export.ts), the extension shown as a fixed suffix that follows the format. The
 * upload never silently overwrites: it is sent with `conflict: 'fail'`, a name already in the folder
 * comes back as a 409, and only the member's "Replace" in the confirm step resends it with
 * `conflict: 'replace'` — no separate exists-then-upload check that another export could race.
 *
 * The folder is shown as ".../<its last folder>" only, a link that reopens the picker ON that folder;
 * the picker carries the same name field, and clicking a document in it takes that document's name
 * (to overwrite or reuse it) instead of opening it. Every folder listing there starts with an "up"
 * row, so the member can climb out of the folder it reopened on (SharePointBrowser `upRow`).
 *
 * Once done, the success view is one sentence, "Artefact exported to <folder> / <file>" (AB#415),
 * both names plain links. On the bid site the folder opens Niobe's own folder browser ON that
 * folder (the picked one, else the default `niobe/exports`), and the file its SharePoint webUrl in
 * a new tab (the system browser on Desktop). A local save can only be reached from Niobe Desktop:
 * the shell records the finished download and opens it by an id it minted
 * (lib/desktop.ts#downloadsBridge) — the folder shows it selected in Explorer/Finder, the file
 * opens in its default app. A plain browser — or an older shell without that bridge — cannot
 * touch the file system, so it keeps the plain "Downloaded to your computer", with no links.
 */

const SCOPES: { id: Scope; label: 'export_scope_part' | 'export_scope_deliverable' | 'export_scope_bid' }[] = [
  { id: 'part', label: 'export_scope_part' },
  { id: 'artefact', label: 'export_scope_deliverable' },
  { id: 'bid', label: 'export_scope_bid' },
]

type FormatLabel =
  | 'export_word_label'
  | 'export_pptx_label'
  | 'export_md_label'
  | 'export_excel_label'
  | 'export_html_label'

const FORMATS: { id: Format; label: FormatLabel }[] = [
  { id: 'docx', label: 'export_word_label' },
  { id: 'pptx', label: 'export_pptx_label' },
  { id: 'html', label: 'export_html_label' },
  { id: 'md', label: 'export_md_label' },
  { id: 'xlsx', label: 'export_excel_label' },
]

/**
 * WHICH document to export, named the way the backend's selector names it
 * (backend/domain/export.ts#selectExportTargets): the room key for a single part, the deliverable
 * id for a whole deliverable, nothing at all for the whole bid (which takes everything).
 *
 * The local download does not need this — `GET /v1/export` derives both from the `name` room key
 * it already sends (routes/export.ts#parseRoomKey). `POST .../sharepoint/export` does NOT derive
 * anything; it hands the body's `artefactId`/`name` straight to the selector. Sending neither is
 * what made every save-to-the-bid-site fail with "No such SharePoint site, library or folder" — a
 * message about SharePoint for a selection that came back empty long before SharePoint was reached.
 */
function selectionOf(room: string, scope: Scope): { artefactId?: string; name?: string } {
  if (scope === 'part') return { name: room }
  if (scope === 'artefact') {
    // "<bid>/<artefact>/<part>" — the middle segment is the deliverable.
    const artefactId = room.split('/')[1]
    return artefactId ? { artefactId } : {}
  }
  return {}
}

/** A destination folder the member picked in the folder picker — enough to export into it, to
 *  show it, and to reopen the picker on it. */
interface Destination {
  driveId: string
  itemId: string
  label: string
  webUrl: string
  location: BrowserLocation
}

/**
 * The default destination's path under the bid's pinned location — the browser-side mirror of
 * backend/app/services.ts DEFAULT_EXPORT_FOLDER_PATH, used only to SHOW it (".../exports") and to
 * open the picker on it when it already exists. The server still creates and picks it.
 */
const DEFAULT_EXPORT_FOLDER = ['niobe', 'exports'] as const

/** A destination's own name — its last folder; null = the default destination. */
function folderName(location: BrowserLocation | null): string {
  return location
    ? (location.folderPath.split('/').filter(Boolean).pop() || location.driveName || location.site.displayName)
    : DEFAULT_EXPORT_FOLDER[1]
}

/** ".../<last folder>" — all the export dialog says about where the file goes (AB#414). */
function shortFolder(location: BrowserLocation | null): string {
  return `.../${folderName(location)}`
}

/** A picked document's name as a base name: the extension is the format's, never typed. */
function baseOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot > 0 ? fileName.slice(0, dot) : fileName
}

/** Why a base name cannot be exported under — both checks, as the server makes them: the base
 *  alone ("x." is refused even though "x..docx" is not) and the full name. */
function nameProblemOf(base: string, suffix: string): 'empty' | 'invalid' | null {
  if (base === '') return 'empty'
  return isSafeItemName(base) && isSafeItemName(base + suffix) ? null : 'invalid'
}

/** Where the picker opens on a chosen folder: that folder itself. */
function startOf(location: BrowserLocation): BrowseStart {
  return {
    siteId: location.site.id,
    siteName: location.site.displayName,
    siteUrl: location.site.webUrl,
    driveId: location.driveId,
    driveName: location.driveName,
    itemId: location.itemId,
    folderPath: location.folderPath,
    webUrl: location.webUrl,
  }
}

/**
 * Where the picker opens with no folder chosen yet: the default `niobe/exports` under the pin when
 * it already exists (walked one folder at a time, as the member could), the pin itself otherwise —
 * a site-only pin, a folder not created yet (first export) or a listing that failed.
 */
async function defaultStart(bid: string, bidSite: BidSite): Promise<BrowseStart> {
  if (!bidSite.driveId) return bidSite
  try {
    let itemId = bidSite.itemId ?? 'root'
    let webUrl = bidSite.webUrl
    for (const name of DEFAULT_EXPORT_FOLDER) {
      const children = await listChildren(bid, bidSite.driveId, itemId)
      const folder = children.find((c) => c.isFolder && c.name.toLowerCase() === name)
      if (!folder) return bidSite
      itemId = folder.id
      webUrl = folder.webUrl
    }
    return {
      ...bidSite,
      itemId,
      webUrl,
      folderPath: [bidSite.folderPath, ...DEFAULT_EXPORT_FOLDER].filter(Boolean).join('/'),
    }
  } catch {
    return bidSite
  }
}

/** Where a folder pane opens: on `at`, else the default destination (see {@link defaultStart}),
 *  resolved once per opening — a new `start` would re-list the pane from scratch mid-browse. */
function useBrowseStart(bid: string, bidSite: BidSite, at: BrowserLocation | null): BrowseStart | null {
  const [start, setStart] = useState<BrowseStart | null>(() => (at ? startOf(at) : null))
  const [initial] = useState({ at, bid, bidSite })
  useEffect(() => {
    if (initial.at) return
    let live = true
    void defaultStart(initial.bid, initial.bidSite).then((s) => {
      if (live) setStart(s)
    })
    return () => {
      live = false
    }
  }, [initial])
  return start
}

/** The bid-site file name: the base-name field, the format's extension as a fixed suffix, and the
 *  inline reason Export is refused. Shared by the export dialog and its folder picker. */
function FileNameField({
  value,
  suffix,
  disabled,
  pending = false,
  onChange,
}: {
  value: string
  suffix: string
  disabled?: boolean
  /** The server's default is still on its way — not an empty name yet. */
  pending?: boolean
  onChange: (value: string) => void
}) {
  const { t } = useLang()
  const id = useId()
  const reason = pending ? null : nameProblemOf(value, suffix)
  return (
    <div className="export-filename">
      <label htmlFor={`${id}-input`}>{t('export_filename_lbl')}</label>
      <div className="export-filename-row">
        <input
          id={`${id}-input`}
          className="prompt-input"
          type="text"
          spellCheck={false}
          value={value}
          disabled={disabled}
          aria-invalid={reason ? true : undefined}
          aria-describedby={reason ? `${id}-ext ${id}-why` : `${id}-ext`}
          onChange={(e) => onChange(e.target.value)}
        />
        <span id={`${id}-ext`} className="export-filename-ext">
          {suffix}
        </span>
      </div>
      {reason && (
        <p id={`${id}-why`} className="export-error">
          {t(reason === 'empty' ? 'export_filename_empty' : 'export_filename_invalid')}
        </p>
      )}
    </div>
  )
}

export function ExportMenu({
  room,
  kind,
  artefactType = 'prose',
  disabled,
}: {
  room: string | null
  kind?: PartKind
  /** The type of the deliverable holding the open part — decides the formats offered. */
  artefactType?: ArtefactType
  disabled?: boolean
}) {
  const { t } = useLang()
  const [open, setOpen] = useState(false)
  const isDisabled = disabled || !room
  // The room key is "bid/artefact/part" — its first segment is the bid the export/settings target.
  const bid = room ? room.split('/')[0]! : null

  return (
    <div className="export-menu">
      <button
        className="doc-top-act"
        aria-haspopup="dialog"
        disabled={isDisabled}
        title={t('export_menu')}
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">⤓</span> {t('export_menu')}
      </button>

      {open && room && bid && (
        <ExportDialog bid={bid} room={room} kind={kind} artefactType={artefactType} onClose={() => setOpen(false)} />
      )}
    </div>
  )
}

function ExportDialog({
  bid,
  room,
  kind,
  artefactType,
  onClose,
}: {
  bid: string
  room: string
  /** The open part's kind — a slides part exports to PowerPoint only at part scope. */
  kind: PartKind | undefined
  /** Its deliverable's type — the backend refuses a format the type does not allow (422). */
  artefactType: ArtefactType
  onClose: () => void
}) {
  const { t } = useLang()
  const { canEdit, isResolved: profileResolved } = useBidProfile(bid)
  // The template library (AB#269) is not a bid: no whole-library export, no SharePoint site, no
  // branding — a template exports by part or by artefact, as a local download.
  const { isLibrary } = useTemplateLibrary(bid)

  // The deliverable's part kinds come off the outline the explorer already caches (same query key
  // as lib/outline.ts and BidHome) — no endpoint of its own. Until it lands, the open part stands in
  // for its deliverable; the backend's 422 stays the safety net either way.
  const docs = useQuery({ queryKey: ['docs', bid], queryFn: () => api.artefacts(bid) })
  const artefactId = room.split('/')[1]
  const deliverable = docs.data?.artefacts.find((a) => a.artefactId === artefactId)
  const deliverableKinds = deliverable ? exportedKinds(deliverable.parts) : null
  const bidMixed = docs.data?.artefacts.some((a) => mixesSlides(exportedKinds(a.parts))) ?? false
  const availability = (s: Scope) => availableFormats(s, kind, artefactType, deliverableKinds, bidMixed)

  // A refused scope is never the active one: the outline can land after the dialog opened on the
  // deliverable (its default) and reveal a mix — the part, always exportable, takes over.
  const [scopeChoice, setScope] = useState<Scope>('artefact')
  const scopeAvail = availability(scopeChoice)
  const scope: Scope = scopeAvail.ok ? scopeChoice : 'part'
  const offered = scopeAvail.ok ? scopeAvail : partFormats(kind, artefactType)
  // null = the scope's default (Excel for a grid, PowerPoint for slides, Word otherwise) — so a
  // default still follows the outline when it lands, until the member picks a format themselves.
  const [format, setFormat] = useState<Format | null>(null)
  const selected: Format = format && offered.formats.includes(format) ? format : offered.preferred
  const pptxOnly = scope !== 'bid' && offered.formats.length === 1 && offered.formats[0] === 'pptx'
  const [destMode, setDestMode] = useState<'site' | 'local'>('local')
  const [destination, setDestination] = useState<Destination | null>(null)
  const [pickingDest, setPickingDest] = useState(false)
  const [busy, setBusy] = useState(false)
  // Guards submit synchronously: a double click within one frame lands before `busy` re-renders.
  const busyRef = useRef(false)
  const [error, setError] = useState('')
  // `at`: the folder the file went to — null = the default destination.
  const [result, setResult] = useState<
    { kind: 'site'; fileName: string; fileUrl: string; at: BrowserLocation | null } | { kind: 'local' } | null
  >(null)
  // The success view's folder link has opened Niobe's folder browser on the bid-site folder.
  const [browsingFolder, setBrowsingFolder] = useState(false)
  // A local save as Niobe Desktop saw it finish — null in a browser, or until the shell says so
  // (the member may still be in the save dialog when the export itself has resolved).
  const [downloads] = useState(downloadsBridge)
  const [saved, setSaved] = useState<FinishedDownload | null>(null)
  // Set while a local export is in flight: the next finished download is that export's file.
  const awaitingDownload = useRef(false)
  const [openFailed, setOpenFailed] = useState(false)
  // null = untouched: the field shows the server's default for the current scope. Once typed, the
  // member's name is kept across format and scope changes — only the suffix follows the format.
  const [typedName, setTypedName] = useState<string | null>(null)
  // The full file name the bid-site upload refused as already there — the confirm step is open.
  const [replacing, setReplacing] = useState<string | null>(null)

  const siteQuery = useQuery({ queryKey: ['bid-site', bid], queryFn: () => api.bidSite(bid), enabled: Boolean(bid) && !isLibrary })
  const bidSite = siteQuery.data?.site ?? null
  const canUseSite = canEdit && Boolean(bidSite)
  const toSite = destMode === 'site' && canUseSite

  const selection = selectionOf(room, scope)
  const defaultName = useQuery({
    queryKey: ['export-name', bid, scope, selection.artefactId ?? selection.name ?? ''],
    queryFn: () => api.spExportName(bid, { scope, ...selection }),
    enabled: toSite,
  })
  const baseName = typedName ?? defaultName.data?.name ?? ''
  // The extension the server appends: a whole-bid export is always a ZIP (lib/download.ts).
  const suffix = scope === 'bid' ? '.zip' : `.${selected}`
  const namePending = baseName === '' && typedName === null && defaultName.isLoading
  const nameProblem = !toSite ? null : namePending ? 'pending' : nameProblemOf(baseName, suffix)
  const folderLabel = destination ? destination.label : t('export_dest_default')

  // Default the destination once BOTH the pinned site and the caller's own profile are known — the
  // bid site when there is one and the member may write to it, local download otherwise (the spec's
  // two "by default" rules). Waiting on `profileResolved` too matters: `useBidProfile`'s own query
  // can still be in flight when the site query settles, and reading `canEdit` before then is
  // indistinguishable from "not an editor", which would default a real editor to local by mistake.
  // Only ONCE past that: either query can refetch behind the member's back (a stale-time refresh,
  // another tab pinning a site), and that must not silently flip a choice already made in this dialog.
  const defaulted = useRef(false)
  useEffect(() => {
    if (defaulted.current || siteQuery.isLoading || !profileResolved) return
    defaulted.current = true
    setDestMode(canUseSite ? 'site' : 'local')
  }, [siteQuery.isLoading, profileResolved, canUseSite])

  useEffect(() => {
    if (!downloads) return
    return downloads.onDone((d) => {
      if (!awaitingDownload.current) return
      awaitingDownload.current = false
      setSaved(d)
    })
  }, [downloads])

  /** Open the saved file, or show it in its folder — a refusal is said, not thrown. */
  function openSaved(how: 'open' | 'reveal'): void {
    if (!downloads || !saved) return
    setOpenFailed(false)
    downloads[how](saved.id)
      .then((r) => setOpenFailed(!r.ok))
      .catch(() => setOpenFailed(true))
  }

  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => prev?.focus()
  }, [])

  /** A refusal is stated in the dialog (server `quiet` writes); anything else is reported as itself. */
  function report(e: unknown): void {
    setError(isApiError(e) && e.serverMessage ? e.serverMessage : e instanceof Error ? e.message : String(e))
  }

  /** While an export runs the dialog cannot be closed (Escape, backdrop, ✕, Cancel). */
  function close(): void {
    if (!busyRef.current) onClose()
  }

  async function submit(conflict: 'fail' | 'replace' = 'fail'): Promise<void> {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setError('')
    try {
      if (destMode === 'local') {
        // downloadExport never throws — a failure is already surfaced by the global error popup
        // (lib/download.ts), so a `false` here just means "stay on the form", nothing more to say.
        // No token passed: downloadBinary resolves a live one, so an export after a long idle
        // does not save a 401 page.
        awaitingDownload.current = true
        setSaved(null)
        const ok = await downloadExport({ room, scope, format: selected })
        if (ok) setResult({ kind: 'local' })
        else awaitingDownload.current = false
      } else {
        const entry = await exportToSharePoint(bid, {
          scope,
          format: selected,
          ...selection,
          ...(destination ? { destination: { driveId: destination.driveId, itemId: destination.itemId } } : {}),
          filename: baseName,
          conflict,
        })
        setResult({ kind: 'site', fileName: entry.name, fileUrl: entry.webUrl, at: destination?.location ?? null })
      }
    } catch (e) {
      // A name already in the folder is a question for the member, not an error.
      if (isApiError(e) && e.status === 409 && e.code === 'sharepoint_conflict' && conflict === 'fail') {
        setReplacing(baseName + suffix)
      } else {
        report(e)
      }
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  function pickedDestination(location: BrowserLocation, name: string): void {
    // The destination browser never confirms the site level itself (a `browse` pane reports no
    // location there) — a site-only pin opens on its libraries and the member picks one.
    if (!location.driveId || !location.itemId) return
    setDestination({
      driveId: location.driveId,
      itemId: location.itemId,
      label: locationLabel(location),
      webUrl: location.webUrl,
      location,
    })
    // Only a name the member changed in the picker (typed, or taken from a document) becomes theirs;
    // an untouched one keeps following the server's default for the scope.
    if (name !== baseName) setTypedName(name)
    setPickingDest(false)
  }

  return (
    <>
      {createPortal(
        <div className="modal-backdrop" onMouseDown={close}>
          <div
            className="modal export-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t('export_menu')}
            aria-busy={busy || undefined}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') close()
            }}
          >
            <div className="modal-head">
              <h2>{t('export_menu')}</h2>
              <button ref={closeRef} className="modal-close" aria-label={t('close')} onClick={close} disabled={busy}>
                ✕
              </button>
            </div>

            <div className="modal-body export-body">
              {result ? (
                <div className="export-success" role="status">
                  {/* One sentence, two plain links — no buttons, no Close (the ✕ closes). */}
                  {result.kind === 'site' ? (
                    <p>
                      {t('export_done_to')}{' '}
                      <button type="button" className="export-link" onClick={() => setBrowsingFolder(true)}>
                        {folderName(result.at)}
                      </button>
                      {' / '}
                      <a className="export-link" href={result.fileUrl} target="_blank" rel="noopener noreferrer">
                        {result.fileName}
                      </a>
                    </p>
                  ) : saved ? (
                    <p>
                      {t('export_done_to')}{' '}
                      <button type="button" className="export-link" onClick={() => openSaved('reveal')}>
                        {saved.folder}
                      </button>
                      {' / '}
                      <button type="button" className="export-link" onClick={() => openSaved('open')}>
                        {saved.filename}
                      </button>
                    </p>
                  ) : (
                    <p>{t('export_dest_local_only')}</p>
                  )}
                  {openFailed && (
                    <p className="export-error" role="alert">
                      {t('export_open_failed')}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <fieldset className="export-fieldset">
                    <legend>{t('export_scope_lbl')}</legend>
                    {SCOPES.filter((s) => !isLibrary || s.id !== 'bid').map((s) => {
                      const avail = availability(s.id)
                      const reasonId = `export-scope-why-${s.id}`
                      return (
                        <div key={s.id}>
                          <label className="export-radio">
                            <input
                              type="radio"
                              name="export-scope"
                              checked={scope === s.id}
                              disabled={!avail.ok || busy}
                              aria-describedby={avail.ok ? undefined : reasonId}
                              onChange={() => {
                                setScope(s.id)
                                // A format the new scope does not take falls back to its default.
                                if (avail.ok && !avail.formats.includes(selected)) setFormat(null)
                              }}
                            />
                            {t(s.label)}
                          </label>
                          {!avail.ok && (
                            <p id={reasonId} className="export-dest-note">
                              {t(avail.reason === 'mixed_bid' ? 'export_scope_mixed_bid' : 'export_scope_mixed')}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </fieldset>

                  <fieldset className="export-fieldset">
                    <legend>{t('export_format_lbl')}</legend>
                    {FORMATS.filter((f) => offered.formats.includes(f.id)).map((f) => (
                      <label key={f.id} className="export-radio">
                        <input
                          type="radio"
                          name="export-format"
                          checked={selected === f.id}
                          disabled={busy}
                          onChange={() => setFormat(f.id)}
                        />
                        {t(f.label)}
                      </label>
                    ))}
                    {pptxOnly && <p className="export-dest-note">{t('export_slides_pptx_only')}</p>}
                    {scope === 'bid' && <p className="export-dest-note">{t('export_bid_slides_note')}</p>}
                  </fieldset>

                  {!isLibrary && (
                  <fieldset className="export-fieldset">
                    <legend>{t('export_dest_lbl')}</legend>
                    <label className="export-radio">
                      <input
                        type="radio"
                        name="export-dest"
                        disabled={!canUseSite || busy}
                        checked={destMode === 'site'}
                        onChange={() => setDestMode('site')}
                      />
                      {t('export_dest_site')}
                    </label>
                    {toSite && (
                      <div className="export-dest-path">
                        <span>{t('export_folder_lbl')}</span>
                        {/* The last folder only; the link reopens the picker on it. The full path
                            stays one hover away. */}
                        <button
                          type="button"
                          className="export-folder-link"
                          title={folderLabel}
                          disabled={busy}
                          onClick={() => setPickingDest(true)}
                        >
                          {shortFolder(destination?.location ?? null)}
                        </button>
                      </div>
                    )}
                    {toSite && (
                      <FileNameField
                        value={baseName}
                        suffix={suffix}
                        disabled={busy}
                        pending={namePending}
                        onChange={setTypedName}
                      />
                    )}
                    <label className="export-radio">
                      <input
                        type="radio"
                        name="export-dest"
                        checked={destMode === 'local'}
                        disabled={busy}
                        onChange={() => setDestMode('local')}
                      />
                      {t('export_dest_local')}
                    </label>
                    {!bidSite && <p className="export-dest-note">{t('bidsite_none')}</p>}
                  </fieldset>
                  )}

                  {!isLibrary && (
                  <button
                    type="button"
                    className="export-brand-link"
                    disabled={busy}
                    onClick={() => {
                      onClose()
                      navigate(settingsPath(bid))
                    }}
                  >
                    {t('export_brand_link')}
                  </button>
                  )}

                  {error && (
                    <p className="export-error" role="alert">
                      {error}
                    </p>
                  )}

                  <p className="visually-hidden" role="status">{busy ? t('export_busy') : ''}</p>

                  <div className="export-acts">
                    <button type="button" className="mini ghost" disabled={busy} onClick={close}>
                      {t('cancel')}
                    </button>
                    <button
                      type="button"
                      className="mini ok export-submit"
                      disabled={busy || nameProblem !== null}
                      onClick={() => void submit()}
                    >
                      {busy && <span className="brand-spinner" aria-hidden="true" />}
                      {t('export_menu')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* A sibling of the export modal, not a child: a portal's React events still bubble to its
          React parent, and Escape here must cancel only this step, not close the export dialog. */}
      {replacing && (
        <ConfirmModal
          title={t('export_replace_title')}
          message={t('export_replace_msg').replace('{name}', replacing).replace('{folder}', folderLabel)}
          confirmLabel={t('export_replace_confirm')}
          danger
          onConfirm={() => {
            setReplacing(null)
            void submit('replace')
          }}
          onClose={() => setReplacing(null)}
        />
      )}

      {browsingFolder && result?.kind === 'site' && bidSite && (
        <FolderBrowser
          bid={bid}
          bidSite={bidSite}
          at={result.at}
          title={folderName(result.at)}
          onClose={() => setBrowsingFolder(false)}
        />
      )}

      {pickingDest && bidSite && (
        <DestinationPicker
          bid={bid}
          bidSite={bidSite}
          at={destination?.location ?? null}
          baseName={baseName}
          suffix={suffix}
          onSelect={pickedDestination}
          onClose={() => setPickingDest(false)}
        />
      )}
    </>
  )
}

/** The folder picker behind the ".../<folder>" link — the same PINNED-folder browser BidSiteCard
 *  itself uses to browse the bid's site (`mode="browse"`), in the app's standard modal shell, opened
 *  on the folder chosen last (else the default `niobe/exports` if it exists, else the pin). There
 *  is no site search here: the bid's site is already chosen, and this dialog only lets the member
 *  pick a different FOLDER within it — folder creation is on (`canCreateFolder`) so they can make
 *  `niobe/exports` or a sibling of it without leaving this dialog. It also holds the file name
 *  (AB#414): a document clicked in the list lends it its name, and the name confirmed here goes
 *  back to the export dialog with the folder; Cancel drops both. */
function DestinationPicker({
  bid,
  bidSite,
  at,
  baseName,
  suffix,
  onSelect,
  onClose,
}: {
  bid: string
  bidSite: BidSite
  /** The folder chosen last; null = the default destination. */
  at: BrowserLocation | null
  baseName: string
  suffix: string
  onSelect: (location: BrowserLocation, name: string) => void
  onClose: () => void
}) {
  const { t } = useLang()
  const [name, setName] = useState(baseName)
  const start = useBrowseStart(bid, bidSite, at)

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal bidsite-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('export_dest_lbl')}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2>{t('export_dest_lbl')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body bidsite-modal-body">
          {start ? (
            <SharePointBrowser
              bid={bid}
              mode="browse"
              start={start}
              canCreateFolder
              upRow
              onPickFile={(entry) => setName(baseOf(entry.name))}
              footer={<FileNameField value={name} suffix={suffix} onChange={setName} />}
              onSelect={(location) => onSelect(location, name)}
              onCancel={onClose}
            />
          ) : (
            <p className="spb-note">{t('loading')}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** The success view's folder link on the bid site (AB#415): Niobe's own browser of the bid's site,
 *  opened ON the folder the file was exported to — browse-only (no "Use this folder", no name
 *  field), so a file in it is a link that opens it in SharePoint in a new tab. */
function FolderBrowser({
  bid,
  bidSite,
  at,
  title,
  onClose,
}: {
  bid: string
  bidSite: BidSite
  /** The folder exported to; null = the default destination. */
  at: BrowserLocation | null
  title: string
  onClose: () => void
}) {
  const { t } = useLang()
  const start = useBrowseStart(bid, bidSite, at)
  // Focus moves in (so Escape closes this pane, not the export dialog under it) and back on close.
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => prev?.focus()
  }, [])
  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal bidsite-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button ref={closeRef} className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body bidsite-modal-body">
          {start ? (
            <SharePointBrowser bid={bid} mode="browse" start={start} upRow />
          ) : (
            <p className="spb-note">{t('loading')}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
